import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Upload } from 'lucide-react';
import { uploadCourseMedia } from '@/lib/api';
import type { Course } from '@/lib/api';
import type { CreateCoursePayload, UpdateCoursePayload } from '@/lib/api';

const MAX_IMAGE_MB = 5;

interface CourseFormProps {
  initial?: Course | null;
  onSave: (payload: CreateCoursePayload | UpdateCoursePayload) => Promise<void>;
  onCancel: () => void;
}

export function CourseForm({ initial, onSave, onCancel }: CourseFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [duration, setDuration] = useState(initial?.duration ?? '');
  const [priceKes, setPriceKes] = useState(initial?.price_kes ?? 0);
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);
  const [imageUrl, setImageUrl] = useState(initial?.image_url ?? '');
  const [cardOverview, setCardOverview] = useState(initial?.card_overview ?? '');
  const [details, setDetails] = useState(initial?.details ?? '');
  const [keyLearningAreas, setKeyLearningAreas] = useState<string[]>(
    (initial?.key_learning_areas && Array.isArray(initial.key_learning_areas)) ? [...initial.key_learning_areas] : ['']
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const addKeyArea = () => setKeyLearningAreas((a) => [...a, '']);
  const removeKeyArea = (i: number) => setKeyLearningAreas((a) => a.filter((_, j) => j !== i));
  const setKeyArea = (i: number, v: string) =>
    setKeyLearningAreas((a) => { const next = [...a]; next[i] = v; return next; });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const areas = keyLearningAreas.map((s) => s.trim()).filter(Boolean);
      const payload = {
        title,
        slug,
        duration: duration || undefined,
        priceKes: Number(priceKes) || 0,
        sortOrder,
        imageUrl: imageUrl.trim() || undefined,
        cardOverview: cardOverview || undefined,
        details: details || undefined,
        keyLearningAreas: areas.length ? areas : undefined,
      };
      await onSave(payload as CreateCoursePayload | UpdateCoursePayload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Slug (URL)</label>
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. my-course" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Duration</label>
          <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 4 weeks" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Price (KES)</label>
          <Input type="number" min={0} value={priceKes || ''} onChange={(e) => setPriceKes(Number(e.target.value) || 0)} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Sort order</label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Card image (courses page)</label>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
              toast.error(`Image must be ${MAX_IMAGE_MB} MB or less.`);
              return;
            }
            setUploadingImage(true);
            try {
              const { url } = await uploadCourseMedia(file);
              setImageUrl(url);
              toast.success('Image uploaded.');
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Upload failed');
            } finally {
              setUploadingImage(false);
              e.target.value = '';
            }
          }}
        />
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={uploadingImage}
            onClick={() => imageInputRef.current?.click()}
          >
            <Upload className="w-3.5 h-3.5" />
            {uploadingImage ? 'Uploading…' : `Upload image (max ${MAX_IMAGE_MB} MB)`}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-1">Or paste URL</p>
        <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Card overview (program page)</label>
        <Textarea value={cardOverview} onChange={(e) => setCardOverview(e.target.value)} rows={3} placeholder="Short blurb for the course card…" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Course details (course page)</label>
        <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} placeholder="Full course details paragraph…" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-foreground">Key learning areas</label>
          <Button type="button" variant="ghost" size="sm" onClick={addKeyArea} className="gap-1">
            <Plus className="w-3.5 h-3.5" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {keyLearningAreas.map((value, i) => (
            <div key={i} className="flex gap-2">
              <Input value={value} onChange={(e) => setKeyArea(i, e.target.value)} placeholder="e.g. Introduction to…" />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeKeyArea(i)} aria-label="Remove">
                <Trash2 className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={saving} className="bg-accent text-accent-foreground hover:bg-gold-light">
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
